import { createContext, useContext, useReducer, useEffect, useCallback } from 'react'
import { authService } from '../services/authService'
import toast from 'react-hot-toast'
import helpers from '../utils/helpers'
import { useNavigate } from 'react-router-dom'
import { dashboardService } from '../services/dashboardService'

const AuthContext = createContext()

const initialState = {
  user: null,
  userRole: null,
  token: helpers.getSession('token'),
  loading: true,
  error: null
}

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        userRole: action.payload.userRole,
        token: action.payload.token,
        loading: false,
        error: null
      }
    case 'LOGIN_FAILURE':
      return { ...state, loading: false, error: action.payload }
    case 'LOGOUT':
      return { ...initialState, loading: false, token: null }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload } }
    default:
      return state
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const navigate = useNavigate()

  useEffect(() => {
    const initializeAuth = async () => {
      const token = helpers.getSession('token')

      if (token) {
        try {
          const user = await authService.getProfile()
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } })
        } catch (error) {
          // localStorage.removeItem('token')
          sessionStorage.removeItem('token');
          dispatch({ type: 'LOGOUT' })
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    initializeAuth()
  }, [])

  const login = async (credentials) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      const response = await authService.login(credentials)

      if (response?.success == true) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data })
        helpers.setSession('token', response?.data?.token, 1)
        // localStorage.setItem('userRole', JSON.stringify(response?.data?.userRole))

        toast.success(response.message)
        navigate('dashboard')
      } else {
        dispatch({ type: 'LOGIN_FAILURE', payload: false })
      }
    } catch (error) {
      const message = error.response?.data?.errors || 'Login failed'
      dispatch({ type: 'LOGIN_FAILURE', payload: false })
      toast.error(message)
      throw error
    }
  }

  const register = async (userData) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      await authService.register(userData)
      toast.success('Registration successful! Please login.')
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed'
      dispatch({ type: 'LOGIN_FAILURE', payload: message })
      toast.error(message)
      throw error
    }
  }

  const logout = async () => {
    // localStorage.removeItem('token')
    try {
      dispatch({ type: 'LOGOUT' })
      const response = await authService.logout()
      if (response?.success == true) {
        sessionStorage.removeItem('token');
        // localStorage.removeItem('userRole')
        toast.success(response.message)
        navigate('/login')
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Logout failed'
      dispatch({ type: 'LOGOUT' })
      toast.error(message)
      throw error
    }
  }

  const updateProfile = async (profileData) => {
    try {
      const updatedUser = await authService.updateProfile(profileData)
      dispatch({ type: 'UPDATE_USER', payload: updatedUser })
      toast.success('Profile updated successfully')
      return updatedUser
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile'
      toast.error(message)
      throw error
    }
  }
  const resetPassword = async (profileData) => {
    try {
      const updatedUser = await authService.resetPassword(profileData)
      dispatch({ type: 'UPDATE_USER', payload: updatedUser })
      console.log(updatedUser,'sdfdgdf')
      // toast.success('Profile updated successfully')
      return updatedUser
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile'
      toast.error(message)
      // throw error
    }
  }

  // 🔹 Forget Password (send reset link)
  const forgotPassword = async (emailOrUsername) => {
    try {
      // dispatch({ type: 'SET_LOADING', payload: true });
      const response = await authService.forgetPassword({ email_or_username: emailOrUsername });
      toast.success(response.message || 'Reset link sent if account exists');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send reset link';
      toast.error(message);
      throw error;
    } finally {
      // dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // 🔹 Validate Token
// 🔹 Validate Token
const validateResetToken = useCallback(async (token) => {
  try {
    const response = await authService.validateToken({ token });
    return response; 
  } catch (error) {
    const message = error.response?.data?.message || 'Invalid or expired token';
    throw new Error(message);
  }
}, []); // no dispatch, no dependencies




  const getAllDashboardData = async () => {
    try {
      const response = await dashboardService.getDashboardData()
      return response
    } catch (error) {
      throw error
    }
  }

  const getUserRole = async () => {
    try {
      const response = await authService.userRole()
      return response
    } catch (error) {
      throw error
    }
  }

  const value = {
    user: state.user,
    userRole: state.userRole,
    token: state.token,
    loading: state.loading,
    error: state.error,
    login,
    register,
    logout,
    updateProfile,
    getAllDashboardData,
    resetPassword,
    forgotPassword,
    validateResetToken,
    getUserRole
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}